import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import OutboundService from '../../ApiServices/Outbound.Service';
import './CollaboratorPopup.css';

interface CollaboratorPopupProps {
    show: boolean;
    onHide: () => void;
    outboundId: string;
    clientName: string;
}

const CollaboratorPopup: React.FC<CollaboratorPopupProps> = ({
    show,
    onHide,
    outboundId,
    clientName
}) => {
    const [emailList, setEmailList] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;
        return emailRegex.test(email);
    };

    // Fetch collaborators when popup opens
    useEffect(() => {
        if (show && outboundId) {
            fetchCollaborators();
        }
    }, [show, outboundId]);

    const fetchCollaborators = async () => {
        setIsLoadingCollaborators(true);
        try {
            const result = await OutboundService.getCollaborators(outboundId);
            if (result && result.status === true) {
                setEmailList(result.data || []);
            } else {
                setEmailList([]);
            }
        } catch (error) {
            console.error('Error fetching collaborators:', error);
            setEmailList([]);
        } finally {
            setIsLoadingCollaborators(false);
        }
    };

    const handleAddEmail = async () => {
        if (!newEmail.trim()) {
            setEmailError('Email is required');
            return;
        }

        if (!validateEmail(newEmail.trim())) {
            setEmailError('Invalid email address');
            return;
        }

        if (emailList.includes(newEmail.trim())) {
            setEmailError('Email already exists');
            return;
        }

        setIsLoading(true);
        try {
            const result = await OutboundService.addCollaborator(outboundId, newEmail.trim());
            if (result && result.status === true) {
                setEmailList([...emailList, newEmail.trim()]);
                setNewEmail('');
                setEmailError('');
            } else {
                setEmailError(result.message || 'Error adding collaborator');
            }
        } catch (error) {
            console.error('Error adding collaborator:', error);
            setEmailError('Error adding collaborator');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveEmail = async (emailToRemove: string) => {
        setIsLoading(true);
        try {
            const result = await OutboundService.removeCollaborator(outboundId, emailToRemove);
            if (result && result.status === true) {
                setEmailList(emailList.filter(email => email !== emailToRemove));
            } else {
                console.error('Error removing collaborator:', result.message);
            }
        } catch (error) {
            console.error('Error removing collaborator:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fs-5">
                    Manage Collaborators - {clientName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4">
                <div className="collaborator-section">
                    <div className="add-email-section mb-4">
                        <label className="form-label fw-semibold mb-2">
                            Add Collaborator Email
                        </label>
                        
                        <div className="input-group">
                            <input
                                type="email"
                                className={`form-control ${emailError ? 'is-invalid' : ''}`}
                                placeholder="Enter email address"
                                value={newEmail}
                                onChange={(e) => {
                                    setNewEmail(e.target.value);
                                    if (emailError) setEmailError('');
                                }}
                                onKeyPress={handleKeyPress}
                                disabled={isLoading}
                            />
                            <Button
                                className="btn btn-success"
                                onClick={handleAddEmail}
                                disabled={!newEmail.trim() || isLoading}
                            >
                                {isLoading ? (
                                    <span className="spinner-border spinner-border-sm me-2" />
                                ) : (
                                    <FontAwesomeIcon icon={faPlus} />
                                )}
                            </Button>
                        </div>
                        {emailError && (
                            <div className="invalid-feedback d-block">{emailError}</div>
                        )}
                    </div>

                    <div className="email-list-section">
                        <label className="form-label fw-semibold mb-2">
                            Current Collaborators ({emailList.length})
                        </label>
                        {isLoadingCollaborators ? (
                            <div className="text-center py-3">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : emailList.length === 0 ? (
                            <div className="no-collaborators">
                                <p className="text-muted mb-0">No collaborators added yet</p>
                            </div>
                        ) : (
                            <div className="email-list">
                                {emailList.map((email, index) => (
                                    <div key={index} className="email-item">
                                        <span className="email-text">{email}</span>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemoveEmail(email)}
                                            className="remove-btn"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <FontAwesomeIcon icon={faTrash} />
                                            )}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-top">
                <Button variant="secondary" onClick={onHide} disabled={isLoading}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CollaboratorPopup; 